import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogHeader, DialogTitle, DialogBody, DialogFooter } from "../../Shared/Dialog";
import Button from "../../Shared/Buttons/Button";
import Input from "../../Shared/Input";
import Label from "../../Shared/Label";
import { Select, SelectTrigger, SelectContent, SelectItem } from "../../Shared/Select";
import { dataService } from "../../../Services/data/data.service";
import { toast } from "react-toastify";

/**
 * @param {boolean}    open
 * @param {function}   onClose
 * @param {function}   onRegistered  - called after successful registration
 * @param {string}     userId
 * @param {Array}      companies     - [{id, name}, ...] companies the user belongs to
 */
function RegisterFlowBoxDeviceDialog({ open, onClose, onRegistered, userId, companies = [] }) {
  const { t } = useTranslation();
  const [imei, setImei] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loading, setLoading] = useState(false);
  const [imeiError, setImeiError] = useState("");
  const [nameError, setNameError] = useState("");
  const [companyError, setCompanyError] = useState("");

  const multiCompany = companies.length > 1;

  useEffect(() => {
    if (!open) return;
    setSelectedCompany(companies.length === 1 ? companies[0] : null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetForm = () => {
    setImei("");
    setDisplayName("");
    setSelectedCompany(null);
    setImeiError("");
    setNameError("");
    setCompanyError("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = () => {
    let valid = true;
    if (!imei.trim()) { setImeiError(t("flowBox.register.imeiRequired")); valid = false; }
    if (!displayName.trim()) { setNameError(t("flowBox.register.displayNameRequired")); valid = false; }
    if (multiCompany && !selectedCompany) { setCompanyError(t("flowBox.register.companyRequired")); valid = false; }
    if (!valid) return;

    const companyId = multiCompany ? selectedCompany.id : (companies[0]?.id || null);

    setLoading(true);
    dataService
      .registerFlowBoxDevice({
        imei: imei.trim(),
        displayName: displayName.trim(),
        companyId,
        insertedByUserId: userId,
      })
      .then(() => {
        handleClose();
        toast.success(t("flowBox.register.registeredSuccess"));
        onRegistered();
      })
      .catch((err) => toast.error(err?.message || t("flowBox.register.registerFailed")))
      .finally(() => setLoading(false));
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs">
      <DialogHeader>
        <DialogTitle>{t("flowBox.register.title")}</DialogTitle>
      </DialogHeader>
      <DialogBody>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {multiCompany && (
            <div className="dialog-field">
              <Label errorText={companyError} required>{t("flowBox.register.company")}</Label>
              <Select
                value={selectedCompany?.id}
                onChange={(val) => {
                  setSelectedCompany(companies.find((c) => c.id === val) || null);
                  setCompanyError("");
                }}
                searchable
                fullwidth
              >
                <SelectTrigger error={!!companyError}>
                  {selectedCompany ? selectedCompany.name : t("flowBox.register.selectCompany")}
                </SelectTrigger>
                <SelectContent>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="dialog-field">
            <Label htmlFor="regImei" errorText={imeiError} required>{t("flowBox.register.imeiLabel")}</Label>
            <Input
              name="regImei"
              value={imei}
              onChange={(e) => { setImei(e.target.value); setImeiError(""); }}
              placeholder={t("flowBox.register.imeiPlaceholder")}
              error={!!imeiError}
              fullwidth
              inputProps={{ maxLength: 15 }}
            />
          </div>
          <div className="dialog-field">
            <Label htmlFor="regDisplayName" errorText={nameError} required>{t("flowBox.register.displayNameLabel")}</Label>
            <Input
              name="regDisplayName"
              value={displayName}
              onChange={(e) => { setDisplayName(e.target.value); setNameError(""); }}
              placeholder={t("flowBox.register.displayNamePlaceholder")}
              error={!!nameError}
              fullwidth
              inputProps={{ maxLength: 100 }}
            />
          </div>
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="light" onClick={handleClose}>{t("common.cancel")}</Button>
        <Button onClick={handleSubmit} disabled={loading} loading={loading}>
          {t("flowBox.register.register")}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

export default RegisterFlowBoxDeviceDialog;
